import type React from "react";

import { OtpStateError } from "@/lib/errors";
import { logError } from "@/lib/logError";
import { reportSilentFailure } from "@/lib/reportSilentFailure";
import { toast } from "@/lib/toast";
import {
  type CreateOtopApiArg,
  type VerifyOtopApiArg,
} from "@generated/redux/services/generated/api";
import { type OtopRequestResponse, OtopRequestState } from "@generated/types";

import { getBeneficiaryDisplayName } from "./getLegalEntityName";

type ResourceType = CreateOtopApiArg["resourceType"];

type TranslationFunction = (
  key: string,
  params?: Record<string, string | number>
) => string;

export type CreateOtopFunction = (arg: CreateOtopApiArg) => {
  unwrap: () => Promise<OtopRequestResponse>;
};

export type VerifyOtopFunction = (arg: VerifyOtopApiArg) => {
  unwrap: () => Promise<OtopRequestResponse>;
};

/**
 * Discriminator returned by `handleOtpError` to let the caller take additional
 * action beyond showing a toast (e.g. redirect the user back to a re-verify step).
 *
 *   - `email-verification-required`: BFF returned the
 *     "Proof of email ownership required" service-exception. The user's
 *     `emailVerificationToken` is missing or expired; they need to re-enter
 *     the registration flow at `/register`.
 *   - `null`: nothing further to do. The toast was the only side effect.
 */
export type HandledOtpError = "email-verification-required" | null;

const EMAIL_VERIFICATION_REQUIRED_PATTERN =
  /proof of email ownership required/i;

/**
 * The single sonner slot every toast raised by the OTP *modal* flow writes to.
 *
 * sonner updates a toast in place when an existing `id` is reused (merging the
 * new type and message) instead of stacking a new one, and un-dismisses the slot
 * if the user had already closed it.
 * Reusing one id therefore makes each stage of the flow SUPERSEDE the previous —
 * "code sent" → "code correct" → the flow's terminal message all occupy one
 * slot, and an error replaces whatever was showing rather than piling under it.
 *
 * Without this, the trust-beneficiary flow put three toasts on screen within a
 * few hundred milliseconds (the last two are one refetch round-trip apart, well
 * inside the 5000ms toast duration set in `app/layout.tsx`) and they overlapped.
 * See UI-116.
 *
 * Deliberately opt-in — `handleOtpError` is shared with the registration
 * email/mobile/processing pages and `pay-confirmation-actions`, which are
 * unrelated flows. Forcing this id on them would let one flow's message silently
 * overwrite another's.
 *
 * Two sonner caveats worth knowing before relying on this slot:
 *
 *   1. `remainingTime` is only reset when a toast's `duration` CHANGES, so a slot
 *      the user hovered (or that ticked down while the tab was hidden) hands its
 *      residual time to the replacement. The message is correct; it may be short.
 *   2. An auto-closed toast lingers in the Toaster's state for
 *      `TIME_BEFORE_UNMOUNT` (200ms) while it animates out. A same-id write
 *      landing in that window merges into the *exiting* element and is dropped
 *      with it — so pass only message + type through this slot, and don't rely
 *      on it for anything the user must not miss.
 */
export const OTP_FLOW_TOAST_ID = "otp-flow";

/** `{ id }` when a slot was requested, else `undefined` (sonner's default: a new toast). */
const toastOpts = (toastId?: string) => (toastId ? { id: toastId } : undefined);

export interface OtpErrorOptions {
  /** Reuse an existing toast slot (see {@link OTP_FLOW_TOAST_ID}). */
  toastId?: string;
}

/**
 * Handles OTP-related errors with HTTP status checking and user-friendly messages.
 * For HTTP 4xx/5xx errors, always shows the translated fallback (never raw backend messages).
 * For non-HTTP errors (e.g. network failures), falls back to API message then translated key.
 *
 * Returns a discriminator string when the error is a known recoverable case
 * that the caller can act on (e.g. re-route the user); `null` otherwise.
 *
 * NOTE: The email-verification-required match is currently done on the
 * BFF/upstream English message. A follow-up will add a typed `type:` field
 * upstream so this becomes locale-stable, but the message string is the only
 * discriminator available today.
 */
export function handleOtpError(
  error: unknown,
  fallbackKey: string,
  tOtp: TranslationFunction,
  values?: Record<string, string | number>,
  options?: OtpErrorOptions
): HandledOtpError {
  const opts = toastOpts(options?.toastId);
  const status = (error as { status?: number })?.status;
  const data = (error as { data?: { message?: string; messages?: string[] } })
    ?.data;
  const apiMessage = data?.messages?.[0] || data?.message;

  // Detect the specific recoverable case: the BFF rejected the SMS-send (or
  // similar) because the upstream proof-of-email-ownership token expired or
  // was missing. The user can recover by re-entering the flow at /register.
  if (
    typeof apiMessage === "string" &&
    EMAIL_VERIFICATION_REQUIRED_PATTERN.test(apiMessage)
  ) {
    toast.error(tOtp("errors.emailVerificationExpired"), opts);
    return "email-verification-required";
  }

  // HTTP 4xx/5xx → always show translated generic message (never expose raw backend responses)
  if (typeof status === "number" && status >= 400 && status < 600) {
    toast.error(tOtp(fallbackKey, values), opts);
    return null;
  }

  // Non-HTTP errors: prefer API message, then translated fallback
  const errorMessage = apiMessage || tOtp(fallbackKey, values);
  toast.error(errorMessage, opts);
  return null;
}

/**
 * Builds OTP message format with beneficiary name if available
 */
export function buildOtpMessageFormat(
  beneficiary: AnyBeneficiary | undefined,
  messageKeyBase: string,
  tOtp: TranslationFunction,
  additionalParams?: Record<string, string | number>
): string {
  const recipientName = getBeneficiaryDisplayName(beneficiary);

  const messageKey = recipientName
    ? `messageFormat.${messageKeyBase}`
    : `messageFormat.${messageKeyBase}Generic`;

  const params = {
    ...(recipientName ? { recipientName } : {}),
    ...(additionalParams || {}),
  };

  return recipientName || additionalParams
    ? tOtp(messageKey, params)
    : tOtp(messageKey);
}

/**
 * Handles non-Completed OtopRequestState errors
 */
export function handleOtopStateError(
  state: "FAILED" | "EXPIRED" | "EXHAUSTED",
  tOtp: TranslationFunction,
  options?: OtpErrorOptions
): never {
  const opts = toastOpts(options?.toastId);

  if (state === OtopRequestState.Failed) {
    toast.error(tOtp("errors.failed"), opts);
    throw new OtpStateError(OtopRequestState.Failed);
  }

  if (state === OtopRequestState.Expired) {
    toast.error(tOtp("errors.expired"), opts);
    throw new OtpStateError(OtopRequestState.Expired);
  }

  // state === OtopRequestState.Exhausted
  toast.error(tOtp("errors.exhausted"), opts);
  throw new OtpStateError(OtopRequestState.Exhausted);
}

/**
 * Checks if an error is from OtopRequestState handling
 */
export function isOtopStateError(error: unknown): boolean {
  const errorMessage = (error as Error).message;
  return (
    errorMessage === OtopRequestState.Failed ||
    errorMessage === OtopRequestState.Expired ||
    errorMessage === OtopRequestState.Exhausted
  );
}

interface CreateOtpHandlerParams {
  beneficiary: AnyBeneficiary | undefined;
  resourceType: ResourceType;
  resourceTypeId: string;
  createOtop: CreateOtopFunction;
  messageKeyBase: string;
  tOtp: TranslationFunction;
  otpRequestIdRef: React.RefObject<string | null>;
  additionalMessageParams?: Record<string, string | number>;
}

/**
 * Creates OTP creation handler for onProceedToCodeEntry
 */
export function createOtpCreationHandler(
  params: CreateOtpHandlerParams
): () => Promise<string> {
  const {
    beneficiary,
    resourceType,
    resourceTypeId,
    createOtop,
    messageKeyBase,
    tOtp,
    otpRequestIdRef,
    additionalMessageParams,
  } = params;

  return async () => {
    try {
      const messageFormat = buildOtpMessageFormat(
        beneficiary,
        messageKeyBase,
        tOtp,
        additionalMessageParams
      );

      const result = await createOtop({
        resourceType,
        resourceTypeId,
        createOtopRequest: {
          messageFormat,
        },
      }).unwrap();

      otpRequestIdRef.current = result.id;

      toast.success(tOtp("success.codeSent"), { id: OTP_FLOW_TOAST_ID });

      return result.expiryTime;
    } catch (error: unknown) {
      logError("Error creating OTP", error, {
        flow: "otp-helpers",
        step: "create",
        resourceType,
        hasBeneficiary: Boolean(beneficiary),
      });
      handleOtpError(error, "errors.createFailed", tOtp, undefined, {
        toastId: OTP_FLOW_TOAST_ID,
      });
      throw error;
    }
  };
}

interface VerifyOtpHandlerParams {
  verifyOtop: VerifyOtopFunction;
  otpRequestIdRef: React.RefObject<string | null>;
  tOtp: TranslationFunction;
  onCompletedAction: () => Promise<void>;
  /** Resolved support email for the `errors.verifyFailed` message. */
  supportEmail: string;
  /**
   * Set when `onCompletedAction` ALWAYS ends in its own terminal toast — the
   * interim "verification code correct" is then suppressed, because a terminal
   * message like "Recipient marked as trusted" already implies the code was
   * correct, and the two land close enough together to overlap on screen
   * (UI-116).
   *
   * Only claim this when EVERY branch of `onCompletedAction` toasts, including
   * its failure path. `useOtpTrustFlow` qualifies: it polls via
   * `pollUntilCondition`, which swallows its own errors, so the action cannot
   * exit without hitting either the success or the poll-timeout toast.
   *
   * The payment flows deliberately leave this off — their completion action
   * shows nothing, so "code correct" IS their terminal message.
   */
  completionOwnsToast?: boolean;
}

/**
 * Creates OTP verification handler for onSubmitCode
 */
export function createOtpVerificationHandler(
  params: VerifyOtpHandlerParams
): (code: string) => Promise<{ state: OtopRequestState }> {
  const {
    verifyOtop,
    otpRequestIdRef,
    tOtp,
    onCompletedAction,
    supportEmail,
    completionOwnsToast = false,
  } = params;

  return async (code: string) => {
    if (!otpRequestIdRef.current) {
      toast.error(tOtp("errors.noRequestId"), { id: OTP_FLOW_TOAST_ID });
      throw new Error("No OTP request ID");
    }

    try {
      const verifyResult = await verifyOtop({
        otopRequestId: otpRequestIdRef.current,
        verifyOtopRequest: { otop: code },
      }).unwrap();

      switch (verifyResult.state) {
        case OtopRequestState.Completed: {
          // Skip this when the completion action reports the outcome itself —
          // otherwise both land in the same slot milliseconds apart and the
          // interim message just flickers past (UI-116).
          if (!completionOwnsToast) {
            toast.success(tOtp("success.codeVerified"), {
              id: OTP_FLOW_TOAST_ID,
            });
          }

          // The server has ALREADY accepted the code by this point, so a failure
          // from here is a THIRD outcome — neither a bad code nor success — and
          // gets its own message per `.claude/rules/general/error-handling-ux.md`
          // → "Differentiate Failure Modes". Both other options mislead: routing
          // it to the catch below says "Failed to verify code", sending the user
          // to burn a second OTP on a verification that already succeeded; and
          // reporting success leaves them with an un-trusted recipient and no
          // hint that anything went wrong.
          //
          // Deliberately NOT gated on `completionOwnsToast`: the payment flows
          // are the silent case, since their interim success toast has already
          // fired and would otherwise stand as the last word.
          try {
            await onCompletedAction();
          } catch (completionError: unknown) {
            logError(
              "OTP completion action failed after successful verification",
              completionError,
              {
                flow: "otp-helpers",
                step: "completion",
                completionOwnsToast,
              }
            );
            // The user is stuck with a half-applied action that throws nothing
            // organic anywhere else — the same watchdog case as the trust-poll
            // exhaustion in `useOtpTrustFlow`. Stable fingerprint so a volume
            // spike groups into one alertable Sentry issue.
            reportSilentFailure({
              message:
                "[OTP] Completion action failed after successful verification",
              level: "warning",
              fingerprint: "otp-completion-action-failed",
              tags: { "otp.completionOwnsToast": String(completionOwnsToast) },
            });
            toast.warning(tOtp("errors.completedButUpdateFailed"), {
              id: OTP_FLOW_TOAST_ID,
            });
          }
          break;
        }

        case OtopRequestState.Failed:
        case OtopRequestState.Expired:
        case OtopRequestState.Exhausted:
          handleOtopStateError(verifyResult.state, tOtp, {
            toastId: OTP_FLOW_TOAST_ID,
          });
        // falls through
        default:
          // Handle unexpected states
          console.error(`Unexpected OTP state: ${verifyResult.state}`);
          throw new Error(`Unexpected OTP state: ${verifyResult.state}`);
      }

      // Cast to expected type - string literals match enum values at runtime
      return verifyResult as { state: OtopRequestState };
    } catch (error: unknown) {
      logError("Error verifying OTP", error, {
        flow: "otp-helpers",
        step: "verify",
        hasOtpRequestId: Boolean(otpRequestIdRef.current),
      });

      if (isOtopStateError(error)) {
        throw error;
      }

      handleOtpError(
        error,
        "errors.verifyFailed",
        tOtp,
        { supportEmail },
        { toastId: OTP_FLOW_TOAST_ID }
      );
      throw error;
    }
  };
}

/**
 * Creates OTP resend handler for onResendCode
 */
export function createOtpResendHandler(
  params: CreateOtpHandlerParams
): () => Promise<string> {
  const {
    beneficiary,
    resourceType,
    resourceTypeId,
    createOtop,
    messageKeyBase,
    tOtp,
    otpRequestIdRef,
    additionalMessageParams,
  } = params;

  return async () => {
    try {
      const messageFormat = buildOtpMessageFormat(
        beneficiary,
        messageKeyBase,
        tOtp,
        additionalMessageParams
      );

      const result = await createOtop({
        resourceType,
        resourceTypeId,
        createOtopRequest: {
          messageFormat,
        },
      }).unwrap();

      otpRequestIdRef.current = result.id;
      toast.success(tOtp("success.codeResent"), { id: OTP_FLOW_TOAST_ID });

      return result.expiryTime;
    } catch (error: unknown) {
      logError("Error resending OTP", error, {
        flow: "otp-helpers",
        step: "resend",
        resourceType,
        hasBeneficiary: Boolean(beneficiary),
      });
      handleOtpError(error, "errors.resendFailed", tOtp, undefined, {
        toastId: OTP_FLOW_TOAST_ID,
      });
      throw error;
    }
  };
}
